import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Pill,
  Activity,
  Bell,
  Clock,
  Sparkles,
  Flame,
  Sun,
  Moon,
  Sunrise,
  ChevronRight,
  Video,
  MapPin,
  MessageCircle,
  ClipboardList,
  FileText,
  Award,
  CalendarClock,
  PlusCircle,
  Salad,
  Brain,
  HeartPulse,
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, Badge, LoadingState, StatusBadge, DoshaTag, Button, JoinGMeetButton, LicensedDoctorBadge, DoctorLicenseModal } from '../../components/common/UI';
import { useAuth } from '../../context/AuthContext';
import { appointmentsAPI, notificationsAPI, aiAPI, usersAPI } from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/patient/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/patient/appointments', label: 'Appointments', icon: Calendar },
  { path: '/patient/prescriptions', label: 'Prescriptions', icon: Pill },
  { path: '/patient/progress', label: 'Therapy Progress', icon: Activity },
  { path: '/patient/notifications', label: 'Notifications', icon: Bell },
];

// ── Helpers ──────────────────────────────────────────────────────────────
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';

const formatDateTime = (date, time) => {
  if (!date) return '—';
  const d = new Date(date);
  const day = d.toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' });
  return time ? `${day}, ${time}` : day;
};

function greetingFor(hour) {
  if (hour < 5) return { text: 'Still up?', Icon: Moon };
  if (hour < 12) return { text: 'Good morning', Icon: Sunrise };
  if (hour < 17) return { text: 'Good afternoon', Icon: Sun };
  return { text: 'Good evening', Icon: Moon };
}

function timeUntil(date, time) {
  if (!date) return null;
  const d = new Date(date);
  if (time) {
    const [h, m] = time.split(':').map(Number);
    d.setHours(h || 0, m || 0, 0, 0);
  }
  const diffMs = d - new Date();
  if (diffMs <= 0) return 'starting now';
  const mins = Math.round(diffMs / 60000);
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `in ${hours} hr${hours > 1 ? 's' : ''}`;
  const days = Math.round(hours / 24);
  return `in ${days} day${days > 1 ? 's' : ''}`;
}

// ── Circular progress ring — replaces a wall of stacked progress bars ─────
const ProgressRing = ({ value, size = 120, stroke = 10, color = '#10b981', trackColor = '#e6f6f0', children }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, value)) / 100) * circumference;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke={trackColor} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
};

// ── Compact metric chip — replaces repeated full-width ProgressBar rows ───
const MetricChip = ({ icon: Icon, label, value, color }) => {
  const colors = {
    teal: { bg: 'bg-teal-50', text: 'text-teal-600', ring: '#0d9488' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: '#3b82f6' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-600', ring: '#d97706' },
  };
  const c = colors[color] || colors.teal;
  return (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3">
      <ProgressRing value={value} size={44} stroke={4} color={c.ring} trackColor="#f0f0f0">
        <Icon className={`w-4 h-4 ${c.text}`} />
      </ProgressRing>
      <div>
        <p className="text-sm font-bold text-gray-800">{value}%</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  );
};

// ── Treatment day tracker — segmented dots instead of a bare "9 of 14" ────
const TreatmentDayTracker = ({ currentDay, totalDays }) => {
  if (!totalDays) return null;
  const segments = Array.from({ length: totalDays }, (_, i) => i + 1);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-gray-700">Day {currentDay} of {totalDays}</p>
        <p className="text-xs text-gray-400">{totalDays - currentDay > 0 ? `${totalDays - currentDay} days left` : 'Complete'}</p>
      </div>
      <div className="flex gap-1">
        {segments.map((d) => (
          <div
            key={d}
            className={`h-2 flex-1 rounded-full ${
              d < currentDay ? 'bg-emerald-500' : d === currentDay ? 'bg-emerald-300 animate-pulse' : 'bg-gray-100'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Quick action — a real shortcut, not a decorative tile ─────────────────
const QuickAction = ({ to, icon: Icon, label, tone = 'emerald' }) => {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-500',
    blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-500',
    violet: 'bg-violet-50 text-violet-600 group-hover:bg-violet-500',
    amber: 'bg-amber-50 text-amber-600 group-hover:bg-amber-500',
  };
  return (
    <Link
      to={to}
      className="group flex flex-col items-center gap-2 rounded-2xl border border-gray-100 p-4 text-center hover:border-emerald-200 hover:shadow-md transition-all"
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${tones[tone]} group-hover:text-white`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-semibold text-gray-600">{label}</p>
    </Link>
  );
};

// ── Next-session spotlight — one focused card instead of a repeated list
// (the full list already lives on the Appointments page) ──────────────────
const NextSessionSpotlight = ({ appointment }) => {
  if (!appointment) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center">
        <CalendarClock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm font-semibold text-gray-600">No session scheduled</p>
        <p className="text-xs text-gray-400 mt-1 mb-4">Book your next consultation to stay on track</p>
        <Link to="/patient/appointments">
          <Button size="sm">
            <PlusCircle className="w-4 h-4 mr-2" />
            Book Appointment
          </Button>
        </Link>
      </div>
    );
  }

  const isOnline = appointment.type?.toLowerCase() === 'online';

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 p-5 text-white">
      <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold uppercase tracking-wider text-emerald-100">Next Session</span>
        <Badge color="white">{timeUntil(appointment.date, appointment.startTime)}</Badge>
      </div>
      <p className="text-lg font-bold">{appointment.therapy?.name || appointment.type}</p>
      <div className="flex items-center gap-2 flex-wrap text-emerald-100 text-sm mt-0.5">
        <span>with Dr. {appointment.doctor?.name} {appointment.doctor?.specialization ? `· ${appointment.doctor.specialization}` : ''}</span>
        {appointment.doctor && <LicensedDoctorBadge doctor={appointment.doctor} onClick={() => onInspectDoctor && onInspectDoctor(appointment.doctor)} />}
      </div>
      <div className="flex items-center gap-4 mt-4 text-sm text-emerald-50">
        <span className="flex items-center gap-1.5">
          <Calendar className="w-4 h-4" />
          {formatDateTime(appointment.date, appointment.startTime)}
        </span>
        <span className="flex items-center gap-1.5">
          {isOnline ? <Video className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
          {isOnline ? 'Online' : appointment.clinicName || 'In-clinic'}
        </span>
      </div>
      <div className="flex items-center justify-between mt-4">
        <Link to="/patient/appointments" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:gap-2.5 transition-all">
          View details <ChevronRight className="w-4 h-4" />
        </Link>
        <JoinGMeetButton appointment={appointment} />
      </div>
    </div>
  );
};

// ── Activity feed — merges notifications into a timeline, not flat cards ──
const ActivityFeed = ({ notifications }) => {
  const typeMeta = {
    appointment: { icon: Calendar, color: 'text-blue-500 bg-blue-50' },
    prescription: { icon: Pill, color: 'text-emerald-500 bg-emerald-50' },
    therapy: { icon: Activity, color: 'text-teal-500 bg-teal-50' },
    alert: { icon: Bell, color: 'text-rose-500 bg-rose-50' },
    reminder: { icon: Clock, color: 'text-amber-500 bg-amber-50' },
    system: { icon: Bell, color: 'text-gray-500 bg-gray-50' },
  };

  if (notifications.length === 0) {
    return <p className="text-gray-400 text-sm text-center py-6">You're all caught up</p>;
  }

  return (
    <div className="space-y-0">
      {notifications.map((n, i) => {
        const meta = typeMeta[n.type] || typeMeta.system;
        return (
          <div key={n._id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                <meta.icon className="w-4 h-4" />
              </div>
              {i < notifications.length - 1 && <div className="w-px flex-1 bg-gray-100 my-1" />}
            </div>
            <div className={`pb-4 ${n.isRead ? 'opacity-60' : ''}`}>
              <p className="text-sm font-semibold text-gray-800">{n.title}</p>
              <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// MAIN
// ============================================================
export default function PatientDashboard() {
  const { user } = useAuth();
  const [liveUser, setLiveUser] = useState(user);
  const [upcoming, setUpcoming] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [aiRec, setAiRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inspectDoctor, setInspectDoctor] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (user?._id) {
          try {
            const uRes = await usersAPI.getById(user._id);
            if (uRes.data.user) setLiveUser(uRes.data.user);
          } catch (e) {}
        }
        const [apptRes, notifRes] = await Promise.all([
          appointmentsAPI.getUpcoming(),
          notificationsAPI.getAll(),
        ]);
        setUpcoming(apptRes.data.appointments || []);
        setNotifications(notifRes.data.notifications?.slice(0, 5) || []);

        const currentProfile = liveUser?.patientProfile || user?.patientProfile;
        if (currentProfile?.dosha) {
          const aiRes = await aiAPI.recommend({ dosha: currentProfile.dosha });
          setAiRec(aiRes.data.recommendations);
        }
      } catch (err) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?._id]);

  const activeUser = liveUser || user;
  const profile = activeUser?.patientProfile || {};
  const progress = profile.therapyProgress || 0;
  const greeting = useMemo(() => greetingFor(new Date().getHours()), []);
  const nextSession = upcoming[0];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // Treatment day count is derived from the current therapy's own start date +
  // duration when the backend provides them; otherwise the tracker is simply
  // omitted rather than showing an invented number.
  const treatmentDay = useMemo(() => {
    const start = profile.currentTherapy?.startDate;
    const totalDays = profile.currentTherapy?.durationDays;
    if (!start || !totalDays) return null;
    const elapsed = Math.max(1, Math.min(totalDays, Math.ceil((Date.now() - new Date(start)) / 86400000) + 1));
    return { current: elapsed, total: totalDays };
  }, [profile.currentTherapy]);

  if (loading) {
    return (
      <DashboardLayout navItems={NAV_ITEMS} role="patient">
        <LoadingState message="Loading your dashboard..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="patient">
      {/* ── Hero: greeting + wellness ring, no duplicate stat tiles below ── */}
      <div className="rounded-2xl border border-gray-100 bg-white p-5 mb-6 flex flex-col md:flex-row items-center gap-6">
        <ProgressRing value={progress} size={104} stroke={9}>
          <div className="text-center">
            <p className="text-xl font-bold text-gray-800">{progress}%</p>
            <p className="text-[10px] text-gray-400 -mt-0.5">journey</p>
          </div>
        </ProgressRing>

        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-2 text-gray-400 text-sm mb-1">
            <greeting.Icon className="w-4 h-4" />
            {greeting.text}
          </div>
          <h1 className="text-2xl font-bold text-gray-800">{activeUser?.name?.split(' ')[0]}</h1>
          <div className="flex items-center justify-center md:justify-start gap-2 mt-2 flex-wrap">
            {profile.dosha && <DoshaTag dosha={profile.dosha} />}
            {profile.currentTherapy?.name && <Badge color="blue">{profile.currentTherapy.name}</Badge>}
            {unreadCount > 0 && (
              <Link to="/patient/notifications">
                <Badge color="red">{unreadCount} new update{unreadCount > 1 ? 's' : ''}</Badge>
              </Link>
            )}
          </div>
        </div>

        {treatmentDay && (
          <div className="w-full md:w-56 flex-shrink-0">
            <TreatmentDayTracker currentDay={treatmentDay.current} totalDays={treatmentDay.total} />
          </div>
        )}
      </div>

      {/* ── Quick actions — functional shortcuts, not decoration ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <QuickAction to="/patient/appointments" icon={PlusCircle} label="Book Session" tone="emerald" />
        <QuickAction to="/patient/prescriptions" icon={FileText} label="Prescriptions" tone="blue" />
        <QuickAction to="/patient/progress" icon={ClipboardList} label="Log Progress" tone="violet" />
        <QuickAction to="/patient/notifications" icon={MessageCircle} label="Messages" tone="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left / main column ── */}
        <div className="lg:col-span-2 space-y-6">
          <NextSessionSpotlight appointment={nextSession} onInspectDoctor={setInspectDoctor} />

          <Card title="Wellness Breakdown" subtitle="How your habits are trending this cycle">
            <div className="grid sm:grid-cols-3 gap-3">
              <MetricChip icon={Salad} label="Diet Compliance" value={64} color="teal" />
              <MetricChip icon={Brain} label="Meditation" value={85} color="blue" />
              <MetricChip icon={HeartPulse} label="Lifestyle Score" value={45} color="amber" />
            </div>
            {progress >= 100 ? (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                <Award className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <p className="text-emerald-700 text-sm font-medium">Therapy completed — congratulations on finishing your plan!</p>
              </div>
            ) : (
              <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3">
                <Flame className="w-5 h-5 text-blue-500 flex-shrink-0" />
                <p className="text-blue-700 text-sm">{100 - progress}% left on your overall journey — keep it up.</p>
              </div>
            )}
          </Card>

          {upcoming.length > 1 && (
            <Card
              title="Also Coming Up"
              subtitle={`${upcoming.length - 1} more session${upcoming.length - 1 > 1 ? 's' : ''} scheduled`}
              action={
                <Link to="/patient/appointments" className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                  View all <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              }
            >
              <div className="space-y-2">
                {upcoming.slice(1, 4).map((appt) => (
                  <div key={appt._id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                    <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-800 text-sm truncate">{appt.therapy?.name || appt.type}</p>
                        {appt.doctor && <LicensedDoctorBadge doctor={appt.doctor} onClick={() => setInspectDoctor(appt.doctor)} />}
                      </div>
                      <p className="text-gray-400 text-xs">{formatDate(appt.date)} · {appt.startTime}</p>
                    </div>
                    <StatusBadge status={appt.status} />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* ── Right sidebar ── */}
        <div className="space-y-6">
          {aiRec && (
            <Card>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm">AI Insights</p>
                  <p className="text-xs text-gray-400">Personalized for your dosha</p>
                </div>
              </div>

              <div className="bg-violet-50 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-violet-700 font-medium">Wellness Score</span>
                  <span className="text-xs font-bold text-violet-700">{100 - aiRec.riskScore}/100</span>
                </div>
                <div className="w-full bg-violet-100 rounded-full h-2">
                  <div className="h-2 rounded-full bg-violet-500 transition-all duration-700" style={{ width: `${100 - aiRec.riskScore}%` }} />
                </div>
              </div>

              {aiRec.therapyRecommendations?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recommended Therapies</p>
                  <div className="flex flex-wrap gap-1.5">
                    {aiRec.therapyRecommendations.map((t) => (
                      <Badge key={t} color="emerald">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {aiRec.dietaryPlan?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Diet Tips</p>
                  <ul className="space-y-1.5">
                    {aiRec.dietaryPlan.slice(0, 3).map((d, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="text-emerald-500 mt-0.5">✓</span>
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {aiRec.disclaimer && <p className="text-[11px] text-gray-400 italic mt-4">{aiRec.disclaimer}</p>}
            </Card>
          )}

          {profile?.assignedDoctor && (
            <Card title="Your Care Team">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {profile.assignedDoctor.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 text-sm truncate">
                    Dr. {profile.assignedDoctor.name}
                  </p>
                  <p className="text-gray-400 text-xs truncate">
                    {profile.assignedDoctor.doctorProfile?.specialization}
                  </p>
                </div>
                <Link to="/patient/appointments" className="text-emerald-600 flex-shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </Link>
              </div>
            </Card>
          )}

          <Card
            title="Recent Activity"
            action={
              unreadCount > 0 && (
                <Link to="/patient/notifications" className="text-xs font-semibold text-emerald-600">
                  See all
                </Link>
              )
            }
          >
            <ActivityFeed notifications={notifications} />
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
